import { _ } from "../../../core/polyglot/polyglot.js";
import abstractKeyFigureDocument from "../shared/abstractKeyFigureDocument.js";
import KeyFigureChartWidget from "../shared/KeyFigureChartWidget.js";
import keyFigureValuesWidget from "./keyFigureValuesWidget.js";
import MultipleKeyFiguresLegendsWidget from "./MultipleKeyFiguresLegendsWidget.js";
import session from "../../../core/session.js";

/**
 * `multipleKeyFiguresDocument` displays multiple key-figures with the abilities
 * to choose from existing presets or by browsing through the KeyFigures groups
 * and individually select the key-figures.
 *
 * @param {object} spec - Parameter object
 * @param {object} my - protected fields holder
 * @return {*}
 */

/**
 * Todo: Add JSDoc comment
 */
function multipleKeyFiguresDocument(spec, my) {
	spec ||= {};
	my ||= {};

	const that = abstractKeyFigureDocument(spec, my);
	my.location = "multiple-kpis";

	that.title = _("Compare KPIs");
	that.subtitle = _("Compare multiple KPIs");

	that.help = function () {
		return [
			{
				title: _("Compare KPIs"),
				content: _(
					"KPIs are often analysed together. Some relationships become very clear that way and makes it easier for the management team to take action.",
				),
			},
			{
				title: _("KPIs"),
				content: _("This graph shows you the KPIs over time."),
				element: "keyfigure-chart",
			},
			{
				title: _("Legend"),
				content: _(
					"One can chose to deactivate KPIs by clicking on their legend. Chosen KPIs are also presented in numbers.",
				),
				element: "keyfigure-legend",
			},
			{
				title: _("KPI selection"),
				content: _(
					"You can set up the KPI combination of your choice that says something important to your management.",
				),
				element: "keyfigure-picker",
			},
		];
	};

	my.keyFigureValues = keyFigureValuesWidget();

	my.keyFigureChart = new KeyFigureChartWidget();
	my.keyFigureChart.initialize();

	my.keyFigureChart.onPointClick.register((point) => {
		const options = point.options;
		const keyFigure = options.data.getComponent();

		my.redirectTo("single-kpi", {
			keyFigure: keyFigure.getId(),
			period: options.period.toString(),
		});
	});

	my.keyFigureLegends = new MultipleKeyFiguresLegendsWidget({
		chart: my.keyFigureChart,
		legendId: "multiple-kpis-legends",
	});

	my.renderContentOn = function (html) {
		html.div(
			{
				"data-help": "keyfigure-chart",
				"data-test": "keyfigure-chart",
			},
			html.h2(_("KPIs")),
			my.keyFigureChart,
		);

		html.div({ "data-help": "keyfigure-legend" }, my.keyFigureLegends);

		html.h2(_("KPIs in number"));
		html.render(my.keyFigureValues);
	};

	/**
	 * Hook function called when the preset changed,
	 * and before the redirection is made
	 */
	my.presetChanged = function () {
		clear();
	};

	/**
	 * Used to set the content of the document subwidgets (not the actions)
	 */
	my.show = function () {
		setChartsValues();
		my.keyFigureValues.setSelectedPeriod(session.getPeriod());
		my.keyFigureLegends.setCurrentPeriod(session.getPeriod());
	};

	/**
	 * Hook function called when the number of periods to show changed
	 */
	my.setNumberOfPeriods = function () {
		setChartsValues();
	};

	/**
	 * Hook function called when the set of keyFigures to display changed
	 *
	 * @param {KeyFigure[]} keyFigures - List of changed keyFigures
	 */
	my.keyFigureChanged = function (keyFigures) {
		my.switchToCustomPreset(keyFigures);
	};

	async function setChartsValues() {
		const keyFigures = my.getVisibleKeyFigures();

		buildLegends(keyFigures);
		if (!keyFigures.length) {
			clear();
			return;
		}
		const company = session.getCompany();
		const period = session.getPeriod();

		const numPeriods = my.previousPeriodPicker.getSelected();
		const lastPeriods = company.getLatestPeriods(numPeriods, period);

		const result = await my.keyFigureResultService.getValuesForPeriods({
			keyFigures,
			periods: lastPeriods,
		});

		my.keyFigureChart.setResult(result);
		my.keyFigureValues.setResult(result);
		my.keyFigureLegends.setResult(result);
	}

	function clear() {
		my.keyFigureChart.clearSeries();
		my.keyFigureLegends.clear();
	}

	function buildLegends(keyFigures) {
		my.keyFigureLegends.setLegendsFor(keyFigures);
	}

	return that;
}

export default multipleKeyFiguresDocument;
